import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box } from '../components/Frame';
import OnboardSteps from '../components/OnboardSteps';
import { useStore } from '../lib/store';
import { splitWords } from '../lib/mnemonic';

// Schermata 24 parole + conferma backup FUSE (prima erano due passaggi:
// /mnemonic e /verify). Le parole restano in chiaro e la conferma resta
// obbligatoria — è solo un passaggio in meno per l'utente.
export default function MnemonicScreen() {
  const navigate = useNavigate();
  const mnemonic = useStore((s) => s.mnemonic);
  const [saved, setSaved] = useState(false);
  const [understood, setUnderstood] = useState(false);

  if (!mnemonic) {
    return (
      <Frame statusLeft="MNEMONIC :: ERROR" statusRight="5G">
        <Header title="// MNEMONIC" amber />
        <Box variant="danger">
          <p className="text-xs">Nessuna frase in sessione. Torna all&apos;inizio.</p>
        </Box>
        <Footer>
          <Btn onClick={() => navigate('/')}>// back</Btn>
        </Footer>
      </Frame>
    );
  }

  const words = splitWords(mnemonic);
  const numberedList = words
    .map((w, i) => `${String(i + 1).padStart(2, '0')}. ${w}`)
    .join('\n');

  const copyToClipboard = () => {
    navigator.clipboard?.writeText(numberedList).then(
      () => alert('parole copiate'),
      () => alert('copia non riuscita, fai uno screenshot')
    );
  };

  const downloadFile = () => {
    const blob = new Blob([
      `# NULLUM :: RECOVERY MNEMONIC\n` +
      `# Generata il ${new Date().toISOString()}\n` +
      `# CONSERVA AL SICURO. NON CONDIVIDERE.\n\n` +
      `${numberedList}\n\n` +
      `# --- versione singola riga (per import) ---\n` +
      `${mnemonic}\n`
    ], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'nullum-mnemonic.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const canContinue = saved && understood;

  return (
    <Frame statusLeft="IDENTITY :: RECOVERY_PHRASE" statusRight="5G">
      <Header title="▲ Le tue 24 parole" crumb="[salvale ora]" amber />
      <OnboardSteps step={2} />

      <Box variant="warn" className="text-[13px] text-ink-dim mb-3">
        <span className="text-amber">!</span> Queste 24 parole <span className="text-ink">SONO</span> la tua identità.
        L&apos;<span className="text-ink">unico</span> modo per rientrare.<br />
        Se le perdi, perdi tutto. Non c&apos;è modo di recuperarle.
      </Box>

      <div className="border border-dashed border-amber rounded-xl p-3 text-[16px]">
        <div className="grid grid-cols-3 gap-x-3 gap-y-1.5">
          {words.map((w, i) => (
            <div key={i} className="flex items-baseline">
              <span className="text-ink-faint w-6 text-right mr-2 select-none">
                {String(i + 1).padStart(2, '0')}.
              </span>
              <span className="text-ink">{w}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 mt-3">
        <Btn variant="ghost" onClick={copyToClipboard}>⧉ Copia</Btn>
        <Btn variant="ghost" onClick={downloadFile}>▼ Scarica .txt</Btn>
      </div>

      {/* Conferma backup (ex schermata "Verifica", ora qui) */}
      <div className="space-y-2 mt-4">
        <label className="flex items-start gap-3 cursor-pointer text-[15px]">
          <input
            type="checkbox"
            checked={saved}
            onChange={(e) => setSaved(e.target.checked)}
            className="mt-1 accent-green-500 w-4 h-4 cursor-pointer"
          />
          <span className={saved ? 'text-ink' : 'text-ink-dim'}>
            Le ho salvate in un posto sicuro
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer text-[15px]">
          <input
            type="checkbox"
            checked={understood}
            onChange={(e) => setUnderstood(e.target.checked)}
            className="mt-1 accent-green-500 w-4 h-4 cursor-pointer"
          />
          <span className={understood ? 'text-ink' : 'text-ink-dim'}>
            Ho capito: se le perdo, perdo tutto, per sempre
          </span>
        </label>
      </div>

      <p className="text-[12px] text-ink-dim mt-2">
        // scrivile a mano o salvale in un file privato · mai via chat, mail o screenshot
      </p>

      <Footer>
        <Btn
          variant={canContinue ? 'primary' : 'ghost'}
          disabled={!canContinue}
          onClick={() => canContinue && navigate('/pin-setup')}
        >
          {canContinue ? 'Continua ▶' : 'Spunta le due caselle'}
        </Btn>
      </Footer>
    </Frame>
  );
}
